/**
 * Prozo's published guidance: a complete packing video is the strongest
 * evidence a merchant can submit, images alone are weaker, and claims with
 * no outer-packaging visual are rejected outright. The scoring encodes that
 * ranking so the UI can push merchants toward video.
 *
 * Phase C6: the win-rate percentages this function used to assert in its
 * labels (~100% / ~50%) are gone — the module no longer claims to predict an
 * outcome, only whether evidence exists and how strong it is. `score`/
 * `level` are unchanged (100/50/0, strong/weak/none) — only the label text
 * changed — which is what shared/evidenceScore.test.js's drift guard checks
 * against, so that guard still passes.
 */
/**
 * The one-word name for each `level`, as the grid's Evidence chip prints it
 * and as the .xlsx export's Evidence column writes it. It lives beside the
 * function that decides the level so a spreadsheet can never grade a
 * shipment's evidence in different words from the screen it came from.
 *
 * Distinct from the `label` returned below, which is the longer sentence the
 * drawer and the Evidence Studio use where there is room to explain.
 */
export const EVIDENCE_LEVEL_LABEL = {
  none: 'No evidence',
  weak: 'Images only',
  strong: 'Video',
}

export function evidenceScore(row) {
  const imgs = row?.merchantImages || 0
  if (row?.hasVideo) return {score: 100, level: 'strong', label: 'Video — the strongest evidence'}
  if (imgs > 0) return {score: 50, level: 'weak', label: 'Images only — weaker evidence'}
  return {score: 0, level: 'none', label: 'No evidence — will be rejected'}
}

/**
 * THE predicate for "does this shipment carry evidence" — at least one photo
 * OR a packing video, never both.
 *
 * Derived from the level evidenceScore() already computes rather than by
 * re-reading merchantImages/hasVideo, so the dispute gate, the grid's Evidence
 * chip, the .xlsx export and freeze/request.js's rowEvidenceState cannot grade
 * the same shipment differently. Every call site asks this one function: a
 * second copy of the rule would drift the moment either side is edited, and a
 * gate that disagreed with the chip beside it would read as a bug in whichever
 * one the merchant happened to believe.
 */
export function hasEvidence(row) {
  return evidenceScore(row).level !== 'none'
}
